import {useTranslation} from 'react-i18next'
import {Chain} from './../state/state'
import {useAppState} from '../state'
import {useNavigation} from 'react-navi'
import {
  TaskTypeKey,
  TaskGroup,
  PossibleFormFields,
  AllTasks,
  AnyTaskPayload,
  PossibleEvent,
  AssetData,
  AnyTaskData,
  AssetType,
  MeasurementMethod,
  FixedEquipmentTypes,
  Equipment
} from '../state/rest'
import {useCallback, useState, useRef, useEffect, useMemo} from 'react'
import {useForm} from 'react-hook-form'
import {useScheduler} from '../Components/Organisms/Scheduler/useScheduler'
import {Site, Location, OrgLevel, OrgLevelType, LocationGroup} from '../state/state'
import {includes, set} from 'lodash'
import ky from 'ky'
import {getWasteUnit} from '../Components/Views/Tasks/CreateWasteTask'
import i18n from 'i18next'
import {DocumentFolder} from '../state/documents/state'
import {CompletedTask, TaskListItemData} from '../state/home/site/state'
import {AppliancesForChainByCategory} from 'state/appliances/state'

export const hasCoolingEventTarget = (taskType: any, suggestions: any[]) => {
  if (taskType && suggestions) {
    return ['cooling', 'automaticCooling'].includes(taskType.id) && suggestions.length > 0
  } else {
    return false
  }
}

export function valueExists(value?: number | string | null) {
  if (value === undefined) {
    return false
  }
  if (value === null) {
    return false
  }
  if (typeof value === 'number' && isNaN(value)) {
    return false
  }
  if (typeof value === 'string' && value.length === 0) {
    return false
  }
  return true
}
export class NumberParser {
  _locale: string
  constructor(locale: string) {
    this._locale = locale
  }
  parse(str: string) {
    const decimal = (1.1).toLocaleString(this._locale).replace(/\d/g, '')
    const group = (1234).toLocaleString(this._locale).replace(/\d/g, '')
    const parsed = str
      .replace(decimal, '.')
      .replace(group, '')
      .trim()

    return valueExists(parsed) ? parseFloat(parsed) : NaN
  }
}
function parseLinks(saved?: Partial<AllTasks>) {
  const firstLink =
    !!saved && !!saved.links && !!saved.links.length
      ? {linkDescription: saved.links[0].description, linkUrl: saved.links[0].url}
      : {}
  return {...saved, ...firstLink}
}
/**
 * A Controller hook for task creation forms
 */
export function useTaskForm(key: TaskTypeKey, saved?: Partial<AllTasks>, isNew?: boolean) {
  const {state, actions} = useAppState()
  const [modalOpen, setModalOpen] = useState(false)

  const backPath = !saved || isNew ? '/create/task' : '/edit/tasks'
  const completedPath = '/edit/tasks'

  const {navigate} = useNavigation()

  async function goBack() {
    navigate(completedPath)
  }
  const {reset, register, watch, errors, triggerValidation, formState, setValue} = useForm<PossibleFormFields>({
    defaultValues: parseLinks(saved),
    mode: 'onChange'
  })

  const [loading, setLoading] = useState(false)
  // watch() switches from defaults to registered fields during initialization.
  // Instructions register in an effect, so watch this field explicitly to retain its default until then.
  const form = {...watch(), instructions: watch('instructions')}

  const {
    scheduledPayload,
    toggleShow,
    handleScheduler,
    reverseSchedule,
    schedulingEnabled,
    selectedFrequency,
    setSelectedFrequency,
    scheduleIncomplete
  } = useScheduler(saved ? saved.schedule : undefined)

  const [lateByDays, setLateByDays] = useState(saved && saved.lateByDays ? saved.lateByDays : 1)

  const [automaticReminder, setAutomaticReminder] = useState(
    saved && saved.automaticReminder !== undefined ? saved.automaticReminder : false
  )

  const [defaultSuggestions, setDefaultSuggestions] = useState(saved ? saved.defaultSuggestions : [])
  const [defaultRecipients, setDefaultRecipients] = useState(saved ? saved.defaultRecipients : [])
  const [defaultSuppliers, setDefaultSuppliers] = useState(saved ? saved.defaultSuppliers : [])
  const [relatedSites, setRelatedSites] = useState(saved ? saved.relatedSites : [])

  // this is weird, but works. See https://github.com/facebook/react/issues/14981
  const [, forceError] = useState()
  const submit = (payload: any) => {
    if (scheduleIncomplete) return
    setLoading(true)

    triggerValidation().then(ok => {
      if (ok) {
        actions
          .createTask({
            ...payload,
            relatedSites,
            name: form.name,
            description: form.description,
            instructions: form.instructions,
            taskType: {id: key},
            automaticReminder: automaticReminder,
            lateByDays: lateByDays,
            schedule: scheduledPayload,
            alarmsEnabled: showAlarm,
            taskGroup: payload.taskGroup ? {id: payload.taskGroup.id} : undefined,
            measurementMethod: payload.measurementMethod ? {id: payload.measurementMethod.id} : undefined,
            // Backend expects assets as an array of {id,name} objects.
            // This includes documents since they are stored as AssetType.DOCUMENT.
            assets: assets && assets.length ? assets.map(a => ({id: a.id, name: a.name})) : null,
            links:
              form.linkUrl && form.linkDescription ? [{url: form.linkUrl, description: form.linkDescription}] : null
          })
          .then(goBack)
          .catch(e =>
            forceError(() => {
              throw new LayoutError(
                'We could not create the task. Please check your inputs for mistakes and missing or invalid values'
              )
            })
          )
      } else console.log('errors', {errors})
    })
  }

  // This is a separate function from submit because bulk creation needs to handle multiple payloads, but the validation logic is the same for all of them, so we can reuse it.
  const submitBulkEquipmentTemperature = (tasks: any[], sharedPayload: any) => {
    if (scheduleIncomplete) return
    setLoading(true)

    triggerValidation().then(ok => {
      if (ok) {
        actions
          .createBulkTaskForApplianceMeasurement({
            shared: {
              ...sharedPayload,
              taskType: {id: key},
              taskGroup: sharedPayload.taskGroup ? {id: sharedPayload.taskGroup.id} : undefined,
              measurementMethod: sharedPayload.measurementMethod ? {id: sharedPayload.measurementMethod.id} : undefined,
              sites: sharedPayload.sites?.map((site: any) => ({id: site.id})),
              schedule: scheduledPayload,
              description: form.description,
              instructions: form.instructions,
              automaticReminder,
              lateByDays,
              assets: assets && assets.length ? assets.map(a => ({id: a.id, name: a.name})) : null,
              links:
                form.linkUrl && form.linkDescription ? [{url: form.linkUrl, description: form.linkDescription}] : null
            },
            tasks
          })
          .then(goBack)
          .catch(e =>
            forceError(() => {
              throw new LayoutError(
                'We could not create the tasks. Please check your inputs for mistakes and missing or invalid values'
              )
            })
          )
          .finally(() => {
            setLoading(false)
          })
      } else {
        setLoading(false)
        console.log('errors', {errors})
      }
    })
  }

  const update = (payload: any) => {
    if (scheduleIncomplete) return
    setLoading(true)
    actions
      .updateTask({
        ...payload,
        id: saved!.id!,
        name: payload.name ?? form.name,
        description: form.description,
        instructions: form.instructions,
        automaticReminder: automaticReminder,
        taskType: {id: key},
        schedule: scheduledPayload,
        taskGroup: payload.taskGroup ? {id: payload.taskGroup.id} : undefined,
        measurementMethod: payload.measurementMethod ? {id: payload.measurementMethod.id} : undefined,
        assets: assets && assets.length ? assets.map(a => ({id: a.id, name: a.name})) : null,
        links: form.linkUrl && form.linkDescription ? [{url: form.linkUrl, description: form.linkDescription}] : null,
        alarmsEnabled: payload.alarmsEnabled ?? showAlarm,
        lateByDays: lateByDays
      })
      .then(goBack)
      .catch(e =>
        forceError(() => {
          throw new LayoutError(
            'We could not update the task. Please check your inputs for mistakes and missing or invalid values'
          )
        })
      )
  }

  const remove = () => {
    setLoading(true)

    actions
      .deleteTask(saved!.id!)
      .then(goBack)
      .catch(e =>
        forceError(() => {
          throw new LayoutError(
            'We could not delete the task. Please try once again, or contact support at servicedesk.cgiaromi.fi@cgi.com'
          )
        })
      )
  }

  const duplicate = () => {
    setLoading(true)
    navigate(`/copy/task/${key!}/${saved!.id!}`)
  }

  const cancel = () => {
    setLoading(true)
    navigate(backPath)
  }

  const [assets, setAssets] = useState<AssetData[]>(saved?.assets ? saved.assets : [])

  const [showAlarm, setShowAlarm] = useState(saved && saved.alarmsEnabled !== undefined ? saved.alarmsEnabled : true)

  const removeAsset = (id: string): void => {
    const newAssets = assets ? assets.filter(a => a.id !== id) : []
    setAssets(newAssets)
  }
  useEffect(() => {
    if (valueExists(form.minValue) && valueExists(form.maxValue)) {
      triggerValidation(['minValue', 'maxValue'])
    }
  }, [form.minValue, form.maxValue])
  const addSelectedAsset = useCallback(
    (asset: AssetData) => {
      const addedAsset = asset
      setAssets([...assets, addedAsset])
    },
    [assets]
  )
  useEffect(() => {
    if (!showAlarm) {
      triggerValidation()
    }
  }, [showAlarm])

  const writableIds = state.me?.accessRights.write.sites || []
  const matchingSites = saved?.sites?.filter(s => writableIds?.includes(s.id))
  const siteIds = saved?.sites?.map(s => s.id)
  const editingDisabled = matchingSites?.length !== siteIds?.length

  return {
    editingDisabled,
    setValue,
    setAutomaticReminder,
    setLateByDays,
    form,
    register,
    submit,
    submitBulkEquipmentTemperature,
    update,
    duplicate,
    remove,
    cancel,
    removeAsset,
    addSelectedAsset,
    scheduledPayload,
    schedulingEnabled,
    selectedFrequency,
    setSelectedFrequency,
    scheduleIncomplete,
    toggleShow,
    handleScheduler,
    reverseSchedule,
    defaultSuggestions,
    setDefaultSuggestions,
    defaultRecipients,
    setDefaultRecipients,
    defaultSuppliers,
    setDefaultSuppliers,
    relatedSites,
    setRelatedSites,
    showAlarm,
    setShowAlarm,
    assets,
    errors,
    hasErrors: !formState.isValid,
    formState,
    triggerValidation,
    loading,
    backPath,
    modalOpen,
    automaticReminder,
    lateByDays,
    setModalOpen,
    reset,
    watch
  }
}

export function useTaskGroupSelect(taskKey: TaskTypeKey, saved?: TaskGroup) {
  const {state} = useAppState()
  const {t} = useTranslation('tasks')
  // gather the options
  // NOTE: Overmind derived state typing is not accurate here; cast to any to avoid TS index issues.
  const taskType = (state.taskTypeById as any)[taskKey]
  const fixedTaskTypeGroups = taskType!.taskTypeGroups
  const fixedTaskGroups: TaskGroup[] = fixedTaskTypeGroups.map((group: any) => {
    return group.taskGroup
  })
  const customTaskGroups = (state.customTaskGroups as unknown) as TaskGroup[]
  const allGroups = [...fixedTaskGroups, ...customTaskGroups]
  const taskGroupOptions = allGroups.map(o => ({option: t(...taskGroupNameArgs(o.name)), id: o.id}))

  // get the default option
  const defaultTaskGroup = fixedTaskTypeGroups.find((tg: any) => {
    return tg.default === true
  })!.taskGroup

  const defaultGroupIndex = taskGroupOptions.findIndex(tg => tg.id === defaultTaskGroup.id)
  const defaultIndex = defaultGroupIndex !== -1 ? defaultGroupIndex : 0
  const [selected, setSelected] = useState(saved ? saved : allGroups[defaultIndex])
  const selectTaskGroup = useCallback(
    (id: string) => {
      setSelected((state.taskGroupById as any)[id])
    },
    [state.taskGroupById]
  )
  return {selectedTaskGroup: selected, selectTaskGroup, taskGroupOptions}
}

export function useMeasurementMethodSelect(defaultOption: string, saved?: MeasurementMethod) {
  const {state} = useAppState()
  const {t} = useTranslation('tasks')
  const measurementOptions = state.initialData.measurementMethods.map(o => ({
    option: t(`tasks:measurementMethods.${o.id}`, o.name),
    id: o.id
  }))
  const defaultMeasurementOptionIndex = measurementOptions.findIndex(tg => tg.id === defaultOption)
  const defaultIndex = defaultMeasurementOptionIndex !== -1 ? defaultMeasurementOptionIndex : 0
  const [selected, setSelected] = useState(saved ? saved : state.initialData.measurementMethods[defaultIndex])
  const selectMethod = useCallback(
    (id: string) => {
      setSelected((state.measurementMethodById as any)[id])
    },
    [state.measurementMethodById]
  )

  return {selectedMeasurementMethod: selected, selectMethod, measurementOptions}
}

// FIXME utils should not proably read data from state
export function useDocumentFolderSelect(defaultOption: string, saved?: DocumentFolder) {
  const {state} = useAppState()
  // FIXME find proper solution for Overmind derived state type not working as expected
  const internalDocumentFolders = (state.v1.documents.internalDocumentFolders as unknown) as DocumentFolder[]
  const documentFolderById: any = state.v1.documents.documentFolderById
  const folderOptions = internalDocumentFolders.map(o => ({option: o.name, id: o.id}))

  const defaultIndex = folderOptions.findIndex(df => df.id === defaultOption) || 0
  const [selected, setSelected] = useState(saved ? saved : internalDocumentFolders[defaultIndex])
  const selectMethod = useCallback(
    (id: string) => {
      setSelected(documentFolderById[id])
    },
    [documentFolderById]
  )

  return {selectedDocumentFolder: selected, selectMethod, folderOptions, setSelected}
}

export function useInstructionsDocumentsFolderSelect(defaultOption: string, saved?: DocumentFolder) {
  const {state} = useAppState()
  // FIXME find proper solution for Overmind derived state type not working as expected
  const documentFolders: DocumentFolder[] = (state.v1.instructions.instructionsFolders as unknown) as DocumentFolder[]
  const documentFolderById: any = state.v1.instructions.documentFolderById
  const folderOptions = documentFolders.map(o => ({option: o.name, id: o.id}))

  const defaultIndex = folderOptions.findIndex(df => df.id === defaultOption) || 0
  const [selected, setSelected] = useState(saved ? saved : documentFolders[defaultIndex])
  const selectMethod = useCallback(
    (id: string) => {
      setSelected(documentFolderById[id])
    },
    [documentFolderById]
  )

  return {selectedDocumentFolder: selected, selectMethod, folderOptions, setSelected}
}

export function useTaskGroupsSelect(defaultSelected?: string[]) {
  const {state} = useAppState()
  const taskGroupOptions = state.initialData.taskGroups.map(o => ({option: o.name, id: o.id}))

  const [selected, setSelected] = useState(
    state.initialData.taskGroups.filter(tg => {
      return includes(defaultSelected ? defaultSelected : taskGroupOptions.map(o => o.id), tg.id)
    })
  )
  const selectMethod = useCallback(
    (ids: string[]) => {
      setSelected(
        state.initialData.taskGroups.filter(tg => {
          return includes(ids, tg.id)
        })
      )
    },
    [state.initialData.taskGroups]
  )

  return {selectedTaskGroups: selected, selectMethod, taskGroupOptions}
}

export interface DeviceTypeObject {
  [key: string]: FixedEquipmentTypes[]
}

export interface EquipmentItem {
  id: string
  name: string
  selected: boolean
  siteName: string
  siteId: string
}

export type EquipmentSelectionMap = Record<string, EquipmentItem[]>

type RHFBridge = {watch: any; setValue: any}

export const useDeviceField = (
  form: RHFBridge,
  savedDevices: Equipment[],
  raw: AppliancesForChainByCategory,
  selectedSiteIds: string[]
) => {
  const prevSiteIdsRef = useRef<string[]>(selectedSiteIds)

  useEffect(() => {
    const prev = new Set(prevSiteIdsRef.current.map(String))
    const next = new Set(selectedSiteIds.map(String))

    // when user removes ALL sites, clear device selections entirely
    if (prev.size > 0 && next.size === 0) {
      setDeviceIds([]) // ← clear
      form.setValue('equipment', [], {shouldDirty: true, shouldValidate: true})
    }

    prevSiteIdsRef.current = selectedSiteIds
  }, [selectedSiteIds])

  const savedIds = useMemo<string[]>(() => (savedDevices ? [...new Set(savedDevices.map(d => String(d.id)))] : []), [
    JSON.stringify((savedDevices ?? []).map(d => String(d.id)).sort())
  ])

  const [deviceIds, setDeviceIds] = useState<string[]>(() => {
    const fromForm = (form.watch?.('equipment') as string[] | undefined) ?? []
    if (fromForm.length) return fromForm.map(String)
    if (savedIds.length) return savedIds
    return []
  })

  useEffect(() => {
    form.setValue('equipment', deviceIds)
  }, [deviceIds])

  const hasHydratedRef = useRef(false)
  useEffect(() => {
    if (hasHydratedRef.current) return
    if (!savedIds.length) return
    setDeviceIds(prev => (prev.length ? prev : savedIds))
    hasHydratedRef.current = true
  }, [savedIds])

  const selectedSet = new Set(selectedSiteIds)

  const filteredByCategory = Object.fromEntries(
    Object.entries(raw).map(([cat, items]) => [
      cat,
      (Array.isArray(items) ? items : []).filter(i => selectedSet.has(String(i.siteId)))
    ])
  ) as AppliancesForChainByCategory

  const validIdSet = new Set<string>()

  Object.values(filteredByCategory).forEach((items: any[]) => items?.forEach(it => validIdSet.add(String(it.id))))

  const optionsReady = validIdSet.size > 0
  useEffect(() => {
    if (!optionsReady) return
    setDeviceIds(prev => {
      const next = prev.filter(id => validIdSet.has(String(id)))
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) return prev
      return next
    })
  }, [optionsReady, validIdSet])

  const idSet = new Set(deviceIds)

  const selectionMapOfDevices = Object.fromEntries(
    Object.entries(filteredByCategory).map(([cat, items]) => [
      cat,
      (Array.isArray(items) ? items : []).map(item => {
        const id = String(item.id)
        return {
          id,
          name: item.name,
          selected: idSet.has(id),
          siteName: item.siteName,
          siteId: item.siteId
        }
      })
    ])
  ) as EquipmentSelectionMap

  const toggleAll = (category: FixedEquipmentTypes) => {
    const items = selectionMapOfDevices[category] || []
    const allSelected = items.length > 0 && items.every(d => idSet.has(d.id))
    setDeviceIds(prev => {
      const next = new Set(prev)
      items.forEach(d => (allSelected ? next.delete(d.id) : next.add(d.id)))
      return Array.from(next)
    })
  }

  const toggleDevices = (_category: FixedEquipmentTypes, ids: string[] | string) => {
    const toToggle = Array.isArray(ids) ? ids : [ids]
    setDeviceIds(prev => {
      const next = new Set(prev)
      toToggle.forEach(id => (next.has(id) ? next.delete(id) : next.add(id)))
      return Array.from(next)
    })
  }

  const resetCategory = (category: FixedEquipmentTypes) => {
    setDeviceIds(prev => {
      const next = new Set(prev)
      ;(selectionMapOfDevices[category] || []).forEach(d => next.delete(d.id))
      return Array.from(next)
    })
  }

  const isSelected = (_category: FixedEquipmentTypes, id: string) => idSet.has(id)
  const getSelectedDevices = () => deviceIds
  const getSelectedDevicesByCategory = (category: FixedEquipmentTypes) =>
    (selectionMapOfDevices[category] || []).filter(item => idSet.has(item.id))

  return {
    selectionMapOfDevices,
    toggleDevices,
    toggleAll,
    isSelected,
    getSelectedDevices,
    resetCategory,
    getSelectedDevicesByCategory
  }
}

export function useRestaurantListField(form: RHFBridge, saved?: Site[]) {
  const initialIds = saved?.length ? saved.map(s => s.id) : []
  const [siteIds, setSiteIds] = useState<string[]>(initialIds)

  useEffect(() => {
    form.setValue('sites', siteIds)
  }, [siteIds])

  const idSet = new Set(siteIds)
  const didInteractRef = useRef(false)

  const register = (id: string) => ({
    name: id,
    checked: idSet.has(id),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      didInteractRef.current = true
      const next = new Set(siteIds)
      e.target.checked ? next.add(id) : next.delete(id)
      setSiteIds(Array.from(next))
    }
  })
  useEffect(() => {
    if (!saved || saved.length === 0) return
    if (didInteractRef.current) return
    const savedIds = saved.map(s => s.id)
    setSiteIds(savedIds)
  }, [JSON.stringify(saved)])

  const list = siteIds.map(id => ({id}))

  const toggle = (id: string) => {
    setSiteIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const set = (id: string, checked: boolean) => {
    setSiteIds(prev => {
      const has = prev.includes(id)
      if (checked && !has) return [...prev, id]
      if (!checked && has) return prev.filter(x => x !== id)
      return prev
    })
  }

  const toggleAll = (all: Site[]) => {
    const allSelected = all.length > 0 && all.every(s => idSet.has(s.id))
    setSiteIds(allSelected ? [] : all.map(s => s.id))
  }
  return {register, list, toggleAll, toggle, set}
}

export function useRestaurantSelect(saved?: Site, options?: {defaultToCurrentSite?: boolean}) {
  const {state} = useAppState()
  const currentSite = state.site
  const defaultToCurrentSite = options?.defaultToCurrentSite ?? true
  // NOTE: Overmind derived state typing is not accurate here; cast to any to avoid TS index issues.
  const sites = normalizeSites((state.chainsById as any)[state.selectedChainId!])
  const initialSite = saved ? saved : defaultToCurrentSite ? currentSite : undefined
  const [selected, setSelected] = useState(initialSite)
  const {register, watch} = useForm({
    defaultValues: {
      siteChoices: initialSite?.id
    }
  })
  const value = watch('siteChoices')

  const handleChange = useCallback(() => {
    setSelected(
      sites.find(site => {
        return site.id === value
      })
    )
  }, [sites, value, setSelected])

  useEffect(() => {
    handleChange()
  }, [handleChange])

  return {register, sites, selectedSite: selected}
}

export function payloadStr2float(payload: any) {
  if (payload.value && payload.value.length && typeof payload.value === 'string') {
    return {
      ...payload,
      value: parseFloat(payload.value)
    }
  }
  return payload
}
export function normalizeSites(chain: Chain) {
  const locGroups = chain.brands.reduce<LocationGroup[]>((a, b) => [...a, ...b.locationGroups], [])
  const locations = locGroups.reduce<Location[]>((a, b) => [...a, ...b.locations], [])
  return locations.reduce<Site[]>((a, b) => [...a, ...b.sites], [])
}

export function normalizeLocations(chain: Chain) {
  const locGroups = chain.brands.reduce<LocationGroup[]>((a, b) => [...a, ...b.locationGroups], [])
  const locations = locGroups.reduce<Location[]>((a, b) => [...a, ...b.locations], [])
  return locations
}

export function normalizeLocationGroups(chain: Chain) {
  const locGroups = chain.brands.reduce<LocationGroup[]>((a, b) => [...a, ...b.locationGroups], [])
  return locGroups
}

export function normalizeBrands(chain: Chain) {
  const brands = chain.brands
  return brands
}

export function normalizeChains(chain: Chain) {
  return chain
}

export function getOrgLevel(ob: OrgLevel, chain: Chain) {
  switch (ob.type) {
    case OrgLevelType.SITE: {
      return normalizeSites(chain).find(site => site.id === ob.id)
    }
    case OrgLevelType.LOCATION: {
      return normalizeLocations(chain).find(location => location.id === ob.id)
    }
    case OrgLevelType.LOCATION_GROUP: {
      return normalizeLocationGroups(chain).find(group => group.id === ob.id)
    }
    case OrgLevelType.BRAND: {
      return normalizeBrands(chain).find(brand => brand.id === ob.id)
    }
    case OrgLevelType.CHAIN: {
      return normalizeChains(chain)
    }
    default: {
      return {chains: [chain]}
    }
  }
}

export function sortByName<T extends {name?: string}>(a: T, b: T) {
  return (a.name || '').localeCompare(b.name || '', undefined, {numeric: true, sensitivity: 'base'})
}

/**
 * Helper function to change empty strings (that should be doubles/integers) to undefined
 * @param value
 */

export function handleFormNumber(value?: string | number | null) {
  if (valueExists(value)) {
    if (typeof value === 'number') {
      return value
    } else if (typeof value === 'string') {
      return parseFloat(value)
    }
  } else return undefined
}

export function convertPayloadTemperature(site: Site, value?: string | number) {
  const unit = site.temperatureUnit
  if (value && typeof value === 'string' && !isNaN(parseFloat(value)) && unit === 'F') {
    return farenheit2celcius(parseFloat(value))
  } else {
    console.error('tried to convert non numeric value', value)
    return value
  }
}
export function convertUnit(site: Site, value?: number) {
  const unit = site.temperatureUnit
  if (value === undefined || value === null) return undefined
  if (unit === 'F') {
    return celcius2farenheit(value)
  } else {
    return value
  }
}
export function reverseConvertUnit(site: Site, value?: number | string) {
  const unit = site.temperatureUnit
  if (value === undefined || value === null) return undefined
  if (unit === 'F') {
    value = typeof value === 'number' ? value : parseFloat(value)
    return farenheit2celcius(value)
  } else {
    value = typeof value === 'number' ? value : parseFloat(value)
    return value
  }
}
export function reverseConvertWeight(site: Site, value?: number | string) {
  if (valueExists(value)) {
    if (site.weightUnit === 'LB') {
      return lbs2kg(Number(value))
    } else return Number(value)
  }
  return undefined
}
/**
 * Function to set localized value string back to float
 * @param value
 */
export function deLocalizeValue(locale: string, value?: string | number) {
  const newValue = typeof value === 'string' ? new NumberParser(locale).parse(value) : value
  return newValue
}

function farenheit2celcius(value: number) {
  return (value - 32) / 1.8
}

function celcius2farenheit(value: number) {
  return 1.8 * value + 32
}

export function lbs2kg(value: number) {
  return value * 0.45359237
}
export function kg2lbs(value: number) {
  return value / 0.45359237
}

export const tempUnitStr = (v: string) => `°${v}`
export const shortTempUnitStr = (v: string) => `°`

export function handleTaskTempValues(task: Partial<AllTasks>, site: Site) {
  return {...task, minValue: convertUnit(site, task.minValue), maxValue: convertUnit(site, task.maxValue)}
}

export function handlePayloadTempValues(payload: AnyTaskPayload, site: Site) {
  return {
    ...payload,
    minValue: convertPayloadTemperature(site, payload.minValue),
    maxValue: convertPayloadTemperature(site, payload.maxValue)
  }
}

export function handleTempEventValues(payload: PossibleEvent, site: Site) {
  return {
    ...payload,
    value: convertPayloadTemperature(site, payload.value),
    coolingValues: payload.coolingValues
      ? payload.coolingValues.map(cv => convertPayloadTemperature(site, cv.value))
      : undefined
  }
}

export function handleWeightValue(site: Site, value?: number | string | null) {
  if (typeof value === 'number') {
    if (site.weightUnit !== 'KG') {
      return kg2lbs(value)
    }
    return value
  }
  return undefined
}

export function getWeightUnitStr(value: string) {
  if (value === 'KG') {
    return i18n.t('common:weightUnits.short.kilogram', 'kg')
  } else if (value === 'LB') return i18n.t('common:weightUnits.short.pound', 'lbs')
  else return ''
}

export function round(value: string | number, precision = 1) {
  if (typeof value === 'string') {
    return parseFloat(parseFloat(value).toFixed(precision))
  }
  return parseFloat(value.toFixed(precision))
}

export function handleUnit(site: Site, taskType: TaskTypeKey) {
  if (
    taskType === 'cooling' ||
    taskType === 'automaticCooling' ||
    taskType === 'temperature' ||
    taskType === 'equipmentTemperature' ||
    taskType === 'dishwasherTemperature'
  ) {
    return `°${site.temperatureUnit}`
  } else return undefined
}

export function handleAnyUnit(site: Site, task: CompletedTask) {
  const taskType = task.taskType.id
  if (
    taskType === 'cooling' ||
    taskType === 'automaticCooling' ||
    taskType === 'temperature' ||
    taskType === 'equipmentTemperature' ||
    taskType === 'dishwasherTemperature'
  ) {
    return `°${site.temperatureUnit}`
  } else if (taskType === 'waste' && task.wasteTaskType) {
    return getWasteUnit(site, task.wasteTaskType as any)
  } else return undefined
}

export const localTempValueRaw = (value: number, site: Site) => {
  if (site.temperatureUnit === 'F') {
    return celcius2farenheit(value)
  } else return value
}
export const localTempValue = (value: number, site: Site) => {
  if (site.temperatureUnit === 'F') {
    return round(celcius2farenheit(value))
  } else return round(value)
}

export const localValue = (value: number, taskType: TaskTypeKey) => {
  if (taskType === 'temperature' || taskType === 'equipmentTemperature' || taskType === 'dishwasherTemperature') {
    return value
  }
}

export const isFloat = (value: string) => RegExp(/^[-0-9,.]*$/g).test(value)
export const handleFloatInput = (value: string) =>
  (isFloat(value) ? value : value.slice(0, value.length - 1)).toString()

export function getSites(chain: Chain): Site[] {
  return chain.brands.flatMap(brand =>
    brand.locationGroups.flatMap(group => group.locations.flatMap(location => location.sites))
  )
}

export function getLocations(chain: Chain): Location[] {
  return chain.brands.flatMap(brand => brand.locationGroups.flatMap(group => group.locations))
}

export function getGroups(chain: Chain): LocationGroup[] {
  return chain.brands.flatMap(brand => brand.locationGroups)
}

// https://stackoverflow.com/questions/4907843/open-a-url-in-a-new-tab-and-not-a-new-window
export function openInNewTab(href: string) {
  Object.assign(document.createElement('a'), {
    target: '_blank',
    href
  }).click()
}

export function generateTaskListHeader(task: TaskListItemData | AnyTaskData) {
  switch (task.taskType.id) {
    case 'equipmentTemperature':
      return i18n.t(...taskGroupNameArgs(task.taskGroup.name)) + (task.equipmentName ? ` - ${task.equipmentName}` : '')
    case 'dishwasherTemperature':
      return i18n.t(...taskGroupNameArgs(task.taskGroup.name)) + (task.name ? ` - ${task.name}` : '')
    case 'waste':
      return i18n.t(...taskGroupNameArgs(task.taskGroup.name)) + (task.target ? ` - ${task.target}` : '')
    case 'hygiene':
      return i18n.t(...taskGroupNameArgs(task.taskGroup.name)) + (task.target ? ` - ${task.target}` : '')
    default:
      return i18n.t(...taskGroupNameArgs(task.taskGroup.name))
  }
}

export function translateTaskGroupName(name: string) {
  return i18n.t(taskGroupNameArgs(name))
}

export const translateTaskType = (taskTypeId: string) => {
  switch (taskTypeId) {
    case 'cooling':
    case 'automaticCooling':
      return i18n.t('tasks:labels.taskType.cooling', 'Cooling')
    case 'equipmentTemperature':
      return i18n.t('tasks:labels.taskType.equipmentTemperature', 'Equipment temperature')
    case 'dishwasherTemperature':
      return i18n.t('tasks:labels.taskType.dishwasherTemperature', 'Dishwasher temperature')
    case 'temperature':
      return i18n.t('tasks:labels.taskType.temperature', 'Temperature')
    case 'waste':
      return i18n.t('tasks:labels.taskType.waste', 'Waste')
    case 'hygiene':
      return i18n.t('tasks:labels.taskType.hygiene', 'Surface hygiene')
    case 'singlechoice':
      return i18n.t('tasks:labels.taskType.singlechoice', 'Single choice')
    case 'todo':
      return i18n.t('tasks:labels.taskType.todo', 'Todo')
    case 'manual':
      return i18n.t('tasks:labels.taskType.manual', 'Manual')
    default:
      return '-'
  }
}

export function useFileUpload({cb, savedAsset}: {savedAsset?: AssetData; cb?: (a?: AssetData) => void}) {
  const fileRef = useRef<HTMLInputElement>(null)

  const {actions} = useAppState()
  const [loading, setLoading] = useState(false)
  const [asset, setAsset] = useState<AssetData | null>(savedAsset || null)
  async function startUpload(file: File) {
    // IMPORTANT: do not clear cb for every file in multiple mode (see below)
    setLoading(true)

    try {
      const data = await actions.createUploadUrl({
        fileName: file.name || 'newImage.jpg',
        fileType: file.type || 'image/jpeg'
      })

      const assetData = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', data.uploadURL)

        xhr.onreadystatechange = async function() {
          if (xhr.readyState !== 4) return

          if (xhr.status === 200) {
            try {
              const newAsset = {
                name: file.name || 'newImage.jpg',
                assetPath: `${data.filePath}`,
                assetMimeType: file.type || 'image/jpeg',
                assetSize: file.size,
                assetType: (file.type || '').startsWith('image')
                  ? AssetType.PHOTO
                  : (file.type || '').startsWith('video')
                  ? AssetType.VIDEO
                  : AssetType.DOCUMENT
              }

              const created = await actions.createAsset(newAsset)
              resolve(created)
            } catch (e) {
              reject(e)
            }
          } else {
            reject(new Error(`S3 upload failed (${xhr.status})`))
          }
        }

        xhr.onerror = () => reject(new Error('S3 upload network error'))
        xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg')
        xhr.send(file)
      })

      setAsset(assetData)
      cb?.(assetData)
      return assetData
    } finally {
      setLoading(false)
    }
  }

  const handleUploadClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target && e.target.files && !!e.target.files.length) {
      const file = e.target.files[0]
      console.debug('handleUploadClick', {file})
      startUpload(file)
    }
  }
  // reset file input after upload so same file can be selected again
  useEffect(() => {
    if (!loading && fileRef.current) {
      try {
        fileRef.current.value = ''
      } catch (e) {
        // ignore
      }
    }
  }, [loading, fileRef])

  return {loading, asset, fileRef, handleUploadClick, startUpload}
}

export class ApiVersionError extends Error {
  originalError?: ky.HTTPError
  constructor(msg: string, originalError?: ky.HTTPError) {
    super()
    this.message = msg
    this.originalError = originalError
  }
}
export class LayoutError extends Error {
  originalError?: ky.HTTPError
  constructor(msg: string, originalError?: ky.HTTPError) {
    super()
    this.message = msg
    this.originalError = originalError
  }
}
/**
 * Formats numbers to localized strings, used in inputs and number displays
 * @param locale
 * @param value
 */
export function localNumberFormat(locale: string, value?: number | string | null) {
  // Preserve a bare sign as a user intent placeholder
  if (typeof value === 'string') {
    const trimmed = value.replace(' ', '').trim()
    if (trimmed === '-' || trimmed === '+') {
      return trimmed
    }
  }
  if (valueExists(value)) {
    value =
      typeof value === 'number'
        ? round(value)
        : new NumberParser(locale).parse((value as string).replace(' ', '').trim())

    // Whoa, apparently toLocaleString changes minus sign unicode, causing parseFloat to produce NaN, thus the replace
    return (value as number).toLocaleString(locale).replace(/[\u2212]/, '-')
  } else {
    return undefined
  }
}

export function nuke() {
  localStorage.clear()
  window.location.reload()
}

/**
 *
 * @param effect a function returning your promise. This prevents promise to be executed until you want it to!
 * @param triggerOnLoad if the promise should resolve immediately or on trigger
 */
export function useData<R>(effect: () => Promise<R>, triggerOnLoad: boolean = true) {
  const [data, setData] = useState<R | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const effectRef = useRef(effect)
  const trigger = useCallback(() => {
    effectRef
      .current()
      .then(data => {
        console.debug('got data', data)
        setLoading(false)
        setData(data)
      })
      .catch(e => {
        console.debug('got error', e)
        setError(false)
        setLoading(false)
      })
  }, [effectRef])
  useEffect(() => {
    if (triggerOnLoad) {
      trigger()
    }
  }, [trigger, triggerOnLoad])
  return {data, error, loading, trigger}
}

export function isInRange(value?: number, min?: number, max?: number) {
  if (valueExists(value)) {
    if (valueExists(min) && valueExists(max)) {
      return value! >= min! && value! <= max!
    } else if (valueExists(min) && !valueExists(max)) {
      return value! >= min!
    } else if (!valueExists(min) && valueExists(max)) {
      return value! <= max!
    }
  }
  return false
}

export function shortTgName(tg: string) {
  return `tasks:taskGroups.${tg.replace(' ', '').toLowerCase()}`
}

// Hook
// export function useWindowSize() {
//   const isClient = typeof window === 'object'
//   const [windowSize, setWindowSize] = useState(getSize)

//   function getSize() {
//     return {
//       width: isClient ? window.innerWidth : undefined,
//       height: isClient ? window.innerHeight : undefined
//     }
//   }
//   useEffect(() => {
//     if (!isClient) {
//       return
//     }
//     function handleResize() {
//       setWindowSize(getSize())
//     }
//     window.addEventListener('resize', handleResize)
//     return () => {
//       window.removeEventListener('resize', handleResize)
//     }
//   }, []) // Empty array ensures that effect is only run on mount and unmount
//   return windowSize
// }

/**
 * Helper function to concatenate task group names. Used to translate fixed names in t function:
 * e.g. t(...taskGroupNameArgs('Audit')).
 * Remember to use "tasks" namespace: const {t} = useTranslation('tasks')!
 * @param name Task group name
 */
export function taskGroupNameArgs(name: string): [string, string] {
  return [`tasks:taskGroups.${name.replace(' ', '').toLowerCase()}`, name]
}

// export function wasteTypesArgs(name: string): [string, string] {
//   return [`appliances:wasteTypes.${name.replace(' ', '').toLowerCase()}`, name]
// }

export const assertUnreachable = (never: never) => {}
